import React from 'react';
import { View, Image, Linking } from 'react-native';
import styled from '@emotion/native';
import { H2, BodyText } from '@/shared/components/StyledText';
import { green, primaryHover, sand } from '@/shared/styles/colors';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { eventConfig, type EventConfig } from './constants';
import { EMME_APP_STORE_URL, IS_WEB } from '@/shared/constants/utility';
import { useWebHoverToggle } from '@/shared/hooks/useWebHoverToggle';

interface FinalCalloutProps {
  config: EventConfig['finalCallout'];
}

const FinalCallout: React.FC<FinalCalloutProps> = ({ config }) => {
  const { isDesktop } = useBreakpoint();
  const { isHovered, webHoverProps } = useWebHoverToggle();

  const handleNormalShowClick = async () => {
    try {
      await Linking.openURL(eventConfig.normalShow.eventbriteUrl);
    } catch (error) {
      console.error('Error opening Eventbrite URL:', error);
    }
  };

  const handleDownloadClick = async () => {
    try {
      await Linking.openURL(EMME_APP_STORE_URL);
    } catch (error) {
      console.error('Error opening app store URL:', error);
    }
  };

  return (
    <Section>
      <Content>
        <Header>
          <Title>
            {isDesktop
              ? config.title
              : config.mobileTitle.map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < config.mobileTitle.length - 1 && '\n'}
                  </React.Fragment>
                ))}
          </Title>
          <Description>
            Don&apos;t shop alone at{' '}
            <DescriptionBold onPress={handleNormalShowClick}>
              Not-A-Normal Show
            </DescriptionBold>
            . Book your personal stylist now and discover pieces you&apos;ll
            love forever.
          </Description>
        </Header>

        <DownloadButton
          accessibilityLabel="Download on the App Store"
          accessibilityRole="button"
          onPress={handleDownloadClick}
          {...webHoverProps}
          style={
            IS_WEB && isHovered ? { backgroundColor: primaryHover } : undefined
          }
        >
          <AppStoreImage
            source={require('@/assets/images/event-landing/DownloadWht.png')}
          />
        </DownloadButton>
      </Content>
    </Section>
  );
};

export default FinalCallout;

// Styled Components
const Section = styled(View)({
  backgroundColor: sand,
  paddingVertical: 80,
  paddingHorizontal: 32,
});

const Content = styled(View)({
  maxWidth: 1200,
  alignSelf: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 24,
});

const Header = styled(View)({
  alignItems: 'center',
  maxWidth: 600,
});

const Title = styled(H2)({
  fontSize: 34,
  color: green,
  textTransform: 'uppercase',
  marginBottom: 24,
  textAlign: 'center',
});

const Description = styled(BodyText)({
  fontSize: 16,
  fontWeight: 'normal',
  color: green,
  textAlign: 'center',
});

const DescriptionBold = styled(BodyText)({
  fontSize: 16,
  fontWeight: 'bold',
  color: green,
  textAlign: 'center',
  textDecorationLine: 'underline',
});

const DownloadButton = styled.TouchableOpacity({
  backgroundColor: green,
  borderRadius: 100,
  width: 308,
  height: 70,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  elevation: 4,
  shadowColor: '#000',
  shadowOffset: {
    width: 0,
    height: 4,
  },
  shadowOpacity: 0.1,
  shadowRadius: 6,
});

const AppStoreImage = styled(Image)({
  width: 154,
  height: 36,
});
